// Coach chat client (Track 5.5).
//
// CRUD calls go to the api function; the SSE chat stream goes to the chat
// function (longer timeout + warm pool). Both are authenticated with the
// signed-in user's Firebase ID token.

import { getIdToken } from "../auth";
import type { CoachMessage, CoachThread } from "./schema";

function apiBase(): string {
  const url = import.meta.env.VITE_API_FUNCTION_URL as string | undefined;
  if (!url) throw new Error("VITE_API_FUNCTION_URL is not set.");
  return url.replace(/\/$/, "");
}

function chatBase(): string {
  const url = import.meta.env.VITE_CHAT_FUNCTION_URL as string | undefined;
  if (!url) throw new Error("VITE_CHAT_FUNCTION_URL is not set.");
  return url.replace(/\/$/, "");
}

async function authHeader(): Promise<HeadersInit> {
  const token = await getIdToken();
  return { Authorization: `Bearer ${token}` };
}

async function jsonHeader(): Promise<HeadersInit> {
  const token = await getIdToken();
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
}

async function check(r: Response): Promise<Response> {
  if (!r.ok) {
    const body = await r.text().catch(() => r.statusText);
    throw new Error(`API ${r.status}: ${body.slice(0, 200)}`);
  }
  return r;
}

// ---------------------------------------------------------------------------
// Thread CRUD
// ---------------------------------------------------------------------------

export async function listThreads(sessionId: string): Promise<CoachThread[]> {
  const r = await check(
    await fetch(`${apiBase()}/canvas/${sessionId}/threads`, {
      headers: await authHeader(),
    }),
  );
  const { threads } = (await r.json()) as { threads: CoachThread[] };
  return threads;
}

export async function createThread(
  sessionId: string,
  title?: string,
): Promise<CoachThread> {
  const r = await check(
    await fetch(`${apiBase()}/canvas/${sessionId}/threads`, {
      method: "POST",
      headers: await jsonHeader(),
      body: JSON.stringify({ title }),
    }),
  );
  const { thread } = (await r.json()) as { thread: CoachThread };
  return thread;
}

export async function renameThread(
  sessionId: string,
  threadId: string,
  title: string,
): Promise<CoachThread> {
  const r = await check(
    await fetch(`${apiBase()}/canvas/${sessionId}/threads/${threadId}`, {
      method: "PATCH",
      headers: await jsonHeader(),
      body: JSON.stringify({ title }),
    }),
  );
  const { thread } = (await r.json()) as { thread: CoachThread };
  return thread;
}

export async function deleteThread(
  sessionId: string,
  threadId: string,
): Promise<void> {
  await check(
    await fetch(`${apiBase()}/canvas/${sessionId}/threads/${threadId}`, {
      method: "DELETE",
      headers: await authHeader(),
    }),
  );
}

export async function listMessages(
  sessionId: string,
  threadId: string,
): Promise<CoachMessage[]> {
  const r = await check(
    await fetch(
      `${apiBase()}/canvas/${sessionId}/threads/${threadId}/messages`,
      { headers: await authHeader() },
    ),
  );
  const { messages } = (await r.json()) as { messages: CoachMessage[] };
  return messages;
}

// ---------------------------------------------------------------------------
// SSE chat stream
// ---------------------------------------------------------------------------

export interface StreamChatThreadCallbacks {
  onToken: (delta: string) => void;
  onDone: (meta: { assistantMessageId?: string; interrupted?: boolean }) => void;
  onError: (err: Error) => void;
}

/** Streams a coach reply over SSE. Persists messages server-side; the caller
 *  receives only the token deltas and a final `done` event with the new
 *  assistantMessageId (or `interrupted: true` if the stream aborted). */
export async function streamChatThread(
  sessionId: string,
  threadId: string,
  content: string,
  cb: StreamChatThreadCallbacks,
  signal?: AbortSignal,
): Promise<void> {
  let response: Response;
  try {
    // Note: do NOT add `Accept: text/event-stream` here. Although the header
    // is CORS-safelisted in spec, some browsers include it in preflight when
    // combined with other non-simple headers, which fails against backends
    // that don't list it in Allow-Headers. The server ignores Accept anyway.
    response = await fetch(
      `${chatBase()}/canvas/${sessionId}/threads/${threadId}/chat`,
      {
        method: "POST",
        headers: await jsonHeader(),
        body: JSON.stringify({ content }),
        signal,
      },
    );
  } catch (err) {
    if ((err as { name?: string })?.name === "AbortError") return;
    cb.onError(err instanceof Error ? err : new Error(String(err)));
    return;
  }

  if (!response.ok || !response.body) {
    const text = await response.text().catch(() => response.statusText);
    cb.onError(new Error(`API ${response.status}: ${text.slice(0, 200)}`));
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      // Split on the SSE event terminator. The trailing partial event (if
      // any) stays in `buf` for the next iteration.
      const events = buf.split("\n\n");
      buf = events.pop() ?? "";
      for (const ev of events) {
        const lines = ev.split("\n");
        const event =
          lines.find((l) => l.startsWith("event: "))?.slice(7).trim() ??
          "message";
        const dataLine = lines.find((l) => l.startsWith("data: "))?.slice(6);
        if (!dataLine) continue;
        if (event === "token") {
          try {
            const { delta } = JSON.parse(dataLine) as { delta: string };
            cb.onToken(delta);
          } catch {
            /* ignore parse */
          }
        } else if (event === "done") {
          try {
            cb.onDone(JSON.parse(dataLine));
          } catch {
            cb.onDone({});
          }
        } else if (event === "error") {
          try {
            const { message } = JSON.parse(dataLine) as { message: string };
            cb.onError(new Error(message));
          } catch {
            cb.onError(new Error("Stream error"));
          }
        }
      }
    }
  } catch (err) {
    if ((err as { name?: string })?.name === "AbortError") return;
    cb.onError(err instanceof Error ? err : new Error(String(err)));
  }
}
